import axios from "axios";
import authService from "./authService";

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL ||
  "https://delivery-bonfimgourmet-244425130782.southamerica-east1.run.app";

const API_BASE = `${BACKEND_URL}/pix`;

/**
 * PIX Service
 * Gerencia todas as operações de PIX gerados
 */
class PixService {
  /**
   * Get authorization headers with JWT token
   * @returns {object} Headers object
   */
  getAuthHeaders() {
    const token = authService.getToken();
    return {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    };
  }

  /**
   * Get all PIX generated
   * @returns {Promise<Array>}
   */
  async getAllPix() {
    try {
      const response = await axios.get(API_BASE, this.getAuthHeaders());
      return response.data?.data || response.data || [];
    } catch (error) {
      console.error("❌ Erro ao buscar PIX:", error);
      throw error;
    }
  }

  /**
   * Mark PIX as received
   * @param {string} txid - Transaction ID
   * @returns {Promise<object>}
   */
  async markAsReceived(txid) {
    try {
      // Try PUT first (more common in CORS), fallback to POST if needed
      const response = await axios.put(
        `${API_BASE}/${txid}/received`,
        {},
        this.getAuthHeaders()
      );
      return response.data?.data || response.data;
    } catch (error) {
      console.error(`❌ Erro ao marcar PIX ${txid} como recebido:`, error);
      throw error;
    }
  }

  /**
   * Confirm payment for an order
   * @param {string} orderCode - Order code
   * @param {object} paymentData - Payment data
   * @returns {Promise<object>}
   */
  async confirmPayment(orderCode, paymentData) {
    try {
      const response = await axios.put(
        `${API_BASE}/${orderCode}/received`,
        paymentData,
        this.getAuthHeaders()
      );
      return response.data?.data || response.data;
    } catch (error) {
      console.error(
        `❌ Erro ao confirmar pagamento do pedido ${orderCode}:`,
        error
      );
      throw error;
    }
  }

  /**
   * Delete PIX
   * @param {string} txid - Transaction ID
   * @returns {Promise<void>}
   */
  async deletePix(txid) {
    try {
      await axios.delete(`${API_BASE}/${txid}`, this.getAuthHeaders());
    } catch (error) {
      console.error(`❌ Erro ao deletar PIX ${txid}:`, error);
      throw error;
    }
  }

  /**
   * Format currency value
   * @param {number} value - Value to format
   * @returns {string}
   */
  formatCurrency(value) {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value || 0);
  }

  /**
   * Format date
   * @param {string} date - Date string
   * @returns {string}
   */
  formatDate(date) {
    if (!date) return "-";
    return new Date(date).toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  /**
   * Copy text to clipboard
   * @param {string} text - Text to copy
   * @returns {Promise<void>}
   */
  async copyToClipboard(text) {
    try {
      await navigator.clipboard.writeText(text);
    } catch (error) {
      console.error("❌ Erro ao copiar para área de transferência:", error);
      throw error;
    }
  }

  /**
   * Format phone number with DDD mask
   * @param {string} phone - Phone number
   * @returns {string}
   */
  formatPhone(phone) {
    if (!phone) return "-";

    // Remove tudo que não é dígito
    const cleaned = phone.replace(/\D/g, "");

    // Verifica se tem DDD (11 dígitos) ou não (9 dígitos)
    if (cleaned.length === 11) {
      // Formato: (11) 91234-5678
      return `(${cleaned.substring(0, 2)}) ${cleaned.substring(
        2,
        7
      )}-${cleaned.substring(7)}`;
    } else if (cleaned.length === 10) {
      // Formato: (11) 1234-5678
      return `(${cleaned.substring(0, 2)}) ${cleaned.substring(
        2,
        6
      )}-${cleaned.substring(6)}`;
    } else if (cleaned.length === 9) {
      // Sem DDD: 91234-5678
      return `${cleaned.substring(0, 5)}-${cleaned.substring(5)}`;
    } else if (cleaned.length === 8) {
      // Sem DDD: 1234-5678
      return `${cleaned.substring(0, 4)}-${cleaned.substring(4)}`;
    }

    return phone;
  }
}

export default new PixService();
